import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MobileStepper,
  Slide,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import makeStyles from "@mui/styles/makeStyles";
import { useSnackbar } from "notistack";
import { Feature, Map } from "ol";
import GeometryType from "ol/geom/GeometryType";
import Point from "ol/geom/Point";
import { Draw, Modify, Snap } from "ol/interaction";
import { Vector } from "ol/source";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import {
  EventCreateDto,
  EventDetailsDto,
  EventEditDto,
  useEventApi,
} from "~/api/GeneratedApi";
import { setEvents } from "~/redux/reducers/dataReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { addEventsToMap } from "../../functions/addEventsToMap";
import { addPortsToMap } from "../../functions/addPortsToMap";
import createEventLayer from "../../functions/createEventLayer";
import { findLayerByTitle } from "../../functions/findLayerByTitle";
import useCreateMap from "../../hooks/useCreateMap";
import { LayerTitle } from "../../types/layers";
import EventForm, { EventFormInputs } from "./EventForm";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "95%",
    maxHeight: "95%",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
  },
  contentContainer: {
    display: "flex",
    height: "80%",
    maxHeight: "80%",
  },
  mapContainer: {
    height: "100%",
    width: "100%",
  },
  drawText: {
    width: "80%",
    margin: `${theme.spacing(1)} auto`,
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  onActionDone: () => void;
  mainMap?: Map;
} & ({ edit: true; event: EventDetailsDto } | { edit?: never; event?: never });

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EventDialog = ({
  open,
  onClose,
  onActionDone,
  edit,
  event,
  mainMap,
}: Props): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("event-dialog");
  const mapRef = useRef<Map>();
  const drawRef = useRef<Draw>();
  const sourceRef = useRef<Vector>();
  const [entered, setEntered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<EventFormInputs>();
  const [drawFeature, setDrawFeature] = useState<Feature<Point>>();
  const [drawDone, setDrawDone] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const apiErrorHandler = useApiErrorHandler();
  const { createMap } = useCreateMap();
  const storeEvents = useAppSelector((s) => s.data.events);
  const storePorts = useAppSelector((s) => s.data.ports);

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const eventApi = useEventApi();
  const dispatch = useAppDispatch();

  useCustomQuery("events", () => eventApi.getEvents(), {
    enabled: open,
    onSuccess: (d) => dispatch(setEvents(d.results)),
  });

  const action = useMutation(
    edit && event
      ? (eventToEdit: EventEditDto) => eventApi.editEvent(eventToEdit, event.id)
      : (newEvent: EventCreateDto) => eventApi.addEvent(newEvent),
  );

  // If editing, set form and draw values to the event to edit
  useEffect(() => {
    if (!edit || !event) return;
    setFormData({
      ...event,
      eventTypeId: event.eventType?.id ?? null,
      mapAreaId: event.mapAreaId ?? null,
    });
    setDrawFeature(
      new Feature<Point>({
        geometry: new Point([event.location.x, event.location.y]),
        name: event.name,
      }),
    );
    setDrawDone(true);
  }, [edit, event]);

  const addMap = useCallback(() => {
    if (containerRef.current && !mapRef.current) {
      mapRef.current = createMap({
        target: containerRef.current,
        includedLayers: [LayerTitle.OSM, LayerTitle.Events, LayerTitle.Ports],
        popup: false,
        existingMap: mapRef.current,
      });
      // Add a layer for drawing the event
      const { layer: drawLayer, source: drawSource } = createEventLayer(true);
      sourceRef.current = drawSource;
      mapRef.current.addLayer(drawLayer);

      // Set the existing events layer as visible, ignoring the actual settings
      findLayerByTitle(mapRef.current, LayerTitle.Events)?.setVisible(true);
      findLayerByTitle(mapRef.current, LayerTitle.Ports)?.setVisible(true);

      drawRef.current = new Draw({
        source: sourceRef.current,
        type: GeometryType.POINT,
      });

      mapRef.current.addInteraction(drawRef.current);

      const snap = new Snap({ source: sourceRef.current });
      mapRef.current.addInteraction(snap);
      const modify = new Modify({ source: sourceRef.current });
      mapRef.current.addInteraction(modify);
      drawRef.current.on("drawend", () => {
        setDrawDone(true);
        if (drawRef.current) {
          mapRef.current?.removeInteraction(drawRef.current);
        }
      });
    }
  }, [createMap]);

  const removeMap = useCallback(() => {
    if (mapRef.current) {
      mapRef.current?.dispose();
      mapRef.current = undefined;
      drawRef.current = undefined;
      sourceRef.current = undefined;
    }
  }, []);

  // If mainMap, zoom to the extents of that when opening
  useEffect(() => {
    if (!mainMap || !entered) return;
    const mainExt = mainMap.getView().calculateExtent();
    mapRef.current?.getView().fit(mainExt);
  }, [mainMap, entered]);

  useEffect(() => {
    if (!entered) {
      return;
    }
    switch (activeStep) {
      case 0: {
        addMap();
        if (mapRef.current) {
          if (storeEvents) {
            const filteredEvents = edit
              ? storeEvents.filter((e) => e.id !== event?.id)
              : storeEvents;
            addEventsToMap(mapRef.current, filteredEvents);
          }
          if (storePorts) {
            addPortsToMap(mapRef.current, storePorts);
          }
        }
        // Zoom to the drawed feature after adding it to the map
        const ext = drawFeature?.getGeometry()?.getExtent();
        if (drawFeature && ext && mapRef.current) {
          sourceRef.current?.addFeature(drawFeature);
          mapRef.current?.getView().fit(ext);
          mapRef.current?.getView().setZoom(18);
          setDrawDone(true);
          if (drawRef.current) {
            mapRef.current?.removeInteraction(drawRef.current);
          }
        }
        break;
      }
      case 1:
        removeMap();
        break;
      case 2: {
        addMap();
        // Zoom to the drawed feature after adding it to the map
        const ext = drawFeature?.getGeometry()?.getExtent();
        if (drawFeature && ext) {
          sourceRef.current?.addFeature(drawFeature);
          mapRef.current?.getView().fit(ext);
        }
        // Make the map "readonly" by removing all interactions and controls

        // Map the controls to a separate array first to avoid mutating the
        // interactions array while looping over them when removing the interactions
        const interactionsToRemove = mapRef.current
          ?.getInteractions()
          .getArray()
          .map((i) => {
            return i;
          });
        mapRef.current
          ?.getControls()
          .forEach((c) => mapRef.current?.removeControl(c));
        interactionsToRemove?.forEach((i) =>
          mapRef.current?.removeInteraction(i),
        );
        mapRef.current?.getView().setZoom(18);
        break;
      }
      default:
        break;
    }
  }, [
    activeStep,
    addMap,
    removeMap,
    drawFeature,
    entered,
    storeEvents,
    storePorts,
    edit,
    event?.id,
  ]);

  const handleClose = () => {
    removeMap();
    onClose();
  };

  const handleNext = () => {
    setActiveStep((old) => old + 1);
  };

  const handleDrawDone = () => {
    const feat = sourceRef.current?.getFeatures()[0] as Feature<Point>;
    setDrawFeature(feat);
    handleNext();
  };

  const handlePrevious = () => {
    setActiveStep((old) => old - 1);
  };

  const handleSave = () => {
    // Send data to backend
    const points = drawFeature?.getGeometry()?.getCoordinates();
    if (!points || !formData || !formData.eventTypeId) return;

    action
      .mutateAsync({
        ...formData,
        eventTypeId: formData.eventTypeId,
        location: { x: points[0], y: points[1] },
        mapAreaId: formData.mapAreaId ?? undefined,
      })
      .then((r) => {
        enqueueSnackbar(t(`apiResponses:${r.resultKey}`), {
          variant: "success",
        });
        onActionDone();
        handleClose();
        setDrawDone(false);
        setFormData(undefined);
        setDrawFeature(undefined);
        setActiveStep(0);
      })
      .catch(apiErrorHandler);
  };

  // For the data form
  const handleSubmit = (d: EventFormInputs) => {
    setFormData(d);
    handleNext();
  };

  const handleClearDrawing = () => {
    sourceRef.current?.clear();
    // Re-add the drawing interaction
    if (drawRef.current) {
      setDrawDone(false);
      mapRef.current?.addInteraction(drawRef.current);
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case 0:
        return <div ref={containerRef} className={classes.mapContainer} />;
      case 1:
        return (
          <Grid item xs={12}>
            <EventForm onSubmit={handleSubmit} defaultValues={formData} />
          </Grid>
        );
      case 2:
        return (
          <>
            <Grid item xs={12} lg={6} style={{ height: "100%" }}>
              <div ref={containerRef} className={classes.mapContainer} />
            </Grid>
            <Grid item xs={12} lg={6} style={{ marginTop: theme.spacing(1) }}>
              <EventForm
                onSubmit={handleSubmit}
                defaultValues={formData}
                readOnly
                small
              />
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  const BackButton = () => {
    switch (activeStep) {
      case 0:
        return <Button onClick={onClose}>{t("general:cancel")}</Button>;
      case 1:
        return (
          <Button onClick={handlePrevious}>{t("general:previous")}</Button>
        );
      case 2:
        return (
          <Button onClick={handlePrevious}>{t("general:previous")}</Button>
        );
      default:
        return null;
    }
  };

  const NextButton = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            onClick={handleDrawDone}
            variant="contained"
            color="secondary"
            disabled={!drawDone}
          >
            {t("general:next")}
          </Button>
        );
      case 1:
        return (
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            form="addEventForm"
          >
            {t("general:next")}
          </Button>
        );
      case 2:
        return (
          <Button
            onClick={handleSave}
            disabled={action.isLoading}
            variant="contained"
            color="secondary"
          >
            {t("general:save")}
          </Button>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (isMedium) {
      switch (activeStep) {
        case 0:
          return t("draw");
        case 1:
          return t("fill-info");
        case 2:
          return t("confirm");
        default:
          return t("error");
      }
    }
    return edit ? t("edit-title") : t("add-title");
  };

  return (
    <Dialog
      onClose={handleClose}
      classes={{ paper: !isMedium ? classes.root : "" }}
      open={open}
      fullWidth
      fullScreen={isMedium}
      TransitionComponent={isMedium ? Transition : undefined}
      maxWidth={!isMedium ? "lg" : undefined}
      TransitionProps={{
        onEntered: () => {
          setEntered(true);
          addMap();
        },

        onExited: () => {
          setEntered(false);
          removeMap();
        },
      }}
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container className={classes.contentContainer}>
          {getContent()}
        </Grid>
        {!isMedium && (
          <Stepper activeStep={activeStep} style={{ padding: 24 }}>
            <Step>
              <StepLabel>{t("draw")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t("fill-info")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t("confirm")}</StepLabel>
            </Step>
          </Stepper>
        )}
        <If condition={activeStep === 0}>
          <Typography className={classes.drawText}>{t("draw-text")}</Typography>
        </If>
        <If condition={activeStep === 0 && drawDone}>
          <Button
            variant="contained"
            classes={{ root: classes.drawActionButton }}
            onClick={handleClearDrawing}
          >
            {t("general:clear")}
          </Button>
        </If>
      </DialogContent>
      {!isMedium ? (
        <DialogActions>
          <BackButton />
          <NextButton />
        </DialogActions>
      ) : (
        <MobileStepper
          steps={3}
          activeStep={activeStep}
          nextButton={<NextButton />}
          backButton={<BackButton />}
          variant="dots"
          position="static"
        />
      )}
    </Dialog>
  );
};

export default EventDialog;
